import logging from "utils/logging";
import _ from "lodash";
import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { styled, keyframes } from "@mui/material/styles";
import SignatureCanvas from "react-signature-canvas";
import { TextField, Button, Typography, Paper, Box } from "@mui/material";
import Alert from "@mui/material/Alert";
import {
  createPlayerForUserInGameAction,
  selectLoadingPlayer,
} from "features/player/playerSlice";
import trimCanvas from "utils/trimCanvas";
import Background from "features/components/BackgroundEffect";
import { BrandLockup } from "components";
import Analytics from "analytics";
import { EVENTS } from "analytics/events";

const log = logging.getLogger("MakePlayer screen");

// Brand constants
const ACCENT_TEAL = '#2ec4b6';
const ACCENT_YELLOW = '#ffe066';
const TEXT_COLOR = '#222';
const CARD_BG = '#fff';
const SIGNATURE_BG = '#1a7a72';
const SIGNATURE_BG_RGBA = { r: 26, g: 122, b: 114, a: 255 };

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(46, 196, 182, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(46, 196, 182, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(46, 196, 182, 0);
  }
`;

const Root = styled('div')({
  width: '100%',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
  overflow: 'auto',
  position: 'relative',
});

const ContentCard = styled(Paper)(({ theme }) => ({
  backgroundColor: CARD_BG,
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  padding: theme.spacing(3, 2),
  maxWidth: 450,
  width: '100%',
  animation: `${fadeInUp} 0.6s ease-out`,
  marginBottom: 80,
}));

const lockupSx = {
  display: 'flex',
  margin: '0 auto 16px',
  animation: `${fadeInUp} 1s cubic-bezier(0.4,0,0.2,1)`,
};

const Title = styled(Typography)({
  fontFamily: "'Gyparody', 'Bangers', 'Impact', sans-serif",
  fontSize: '1.75rem',
  fontWeight: 800,
  color: TEXT_COLOR,
  marginBottom: 16,
  textAlign: 'center',
  textShadow: '1px 1px 0px rgba(46, 196, 182, 0.3)',
});

const SignatureCanvasContainer = styled('div')({
  width: '100%',
  height: 200,
  border: `2px solid ${ACCENT_TEAL}`,
  backgroundColor: SIGNATURE_BG,
  borderRadius: 12,
  position: 'relative',
  overflow: 'hidden',
});

const ClearButton = styled(Button)({
  color: ACCENT_TEAL,
  fontSize: '0.9rem',
  textTransform: 'none',
  fontWeight: 600,
  marginTop: 4,
});

const JoinButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(120deg, ${ACCENT_YELLOW} 0%, ${ACCENT_TEAL} 50%, ${ACCENT_YELLOW} 100%)`,
  backgroundSize: '200% 200%',
  backgroundPosition: '0% 40%',
  color: TEXT_COLOR,
  padding: theme.spacing(1.5, 4),
  borderRadius: 12,
  fontWeight: 700,
  fontSize: '1.2em',
  textTransform: 'none',
  boxShadow: '0 4px 16px rgba(44,62,80,0.40) !important',
  minWidth: 240,
  position: 'fixed',
  bottom: theme.spacing(4),
  left: '50%',
  transform: 'translateX(-50%)',
  transition: 'background-position 0.5s cubic-bezier(0.4,0,0.2,1), box-shadow 0s',
  animation: `${pulse} 2s infinite`,
  '&:hover': {
    backgroundPosition: '100% 60%',
    boxShadow: '0 4px 16px rgba(44,62,80,0.40) !important',
  },
  '&.Mui-disabled': {
    background: '#e8e0c0',
    color: '#777570',
    boxShadow: 'none !important',
    cursor: 'not-allowed',
    animation: 'none',
  },
}));

const MakePlayer = ({ gameId, userId }) => {
  const dispatch = useDispatch();

  const signaturePad = useRef(null);
  const containerRef = useRef(null);

  const playerIsLoading = useSelector(selectLoadingPlayer);

  const [creatingPlayer, setCreatingPlayer] = useState(false);
  const [name, setName] = useState("");
  const [signError, setSignError] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setCanvasSize({ width: clientWidth, height: clientHeight });

        // If signature pad exists, clear and resize it
        if (signaturePad.current) {
          signaturePad.current.clear();
        }
      }
    };

    // Initial size
    updateCanvasSize();

    // Add resize listener
    window.addEventListener('resize', updateCanvasSize);

    // Cleanup
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  const trimSignature = () => {
    const sigPad = signaturePad.current;
    if (!sigPad) {
      return;
    }

    const canvas = sigPad.getCanvas();

    log.debug("Got canvas: ", canvas);

    // Create a copy of the canvas
    let copy = document.createElement("canvas");
    copy.width = canvas.width;
    copy.height = canvas.height;
    const ctx = copy.getContext("2d");

    // First fill with the teal background
    ctx.fillStyle = SIGNATURE_BG;
    ctx.fillRect(0, 0, copy.width, copy.height);

    // Then draw the signature canvas on top
    ctx.drawImage(canvas, 0, 0);

    // then trim it
    return trimCanvas(copy, SIGNATURE_BG_RGBA);
  };

  async function onCreatePlayer() {
    let signatureURL = null;

    if (signaturePad.current && signaturePad.current.isEmpty()) {
      log.debug("No signature");
      setSignError(true);
      return;
    }

    const trimmed = trimSignature();
    if (!trimmed) {
      log.warn("Signature trimming failed");
      setSignError(true);
      return;
    }

    log.debug("Trimmed signature URL:", trimmed.toDataURL());
    signatureURL = trimmed.toDataURL("image/jpeg");

    setCreatingPlayer(true);

    Analytics.track(EVENTS.PLAYER_NAME_SUBMITTED, {
      name_length: name.trim().length,
    });

    try {
      await dispatch(
        createPlayerForUserInGameAction(userId, gameId, name.trim(), signatureURL)
      );
    } catch (err) {
      log.error("Failed to create player:", err);
      setCreatingPlayer(false);
    }
  }

  const resetSignature = () => {
    signaturePad.current.clear();
  };

  let disabled = false;
  let buttonCopy = "Join Game";

  if (_.isEmpty(name)) {
    disabled = true;
  } else if (creatingPlayer || playerIsLoading) {
    disabled = true;
    buttonCopy = "Joining game...";
  }

  log.debug("Joining: ", creatingPlayer, playerIsLoading, name, disabled);

  return (
    <Root>
      <Background />
      <ContentCard>
        <BrandLockup size="md" variant="light" sx={lockupSx} />
        <Title>Join the Game!</Title>

        <Box sx={{ px: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 500, color: TEXT_COLOR, mb: 1 }}>
            What should we call you?
          </Typography>
          <TextField
            fullWidth
            id="player-name"
            label="Name"
            placeholder="e.g. Alex"
            variant="outlined"
            onChange={(e) => setName(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: ACCENT_TEAL,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: ACCENT_TEAL,
              },
            }}
          />

          <Typography variant="body1" sx={{ fontWeight: 500, color: TEXT_COLOR, mb: 1 }}>
            Draw your signature:
          </Typography>
          <SignatureCanvasContainer ref={containerRef}>
            <SignatureCanvas
              ref={signaturePad}
              penColor="white"
              backgroundColor={SIGNATURE_BG}
              canvasProps={{
                width: canvasSize.width,
                height: canvasSize.height,
                className: 'signature-canvas',
                style: {
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }
              }}
            />
          </SignatureCanvasContainer>
          <ClearButton onClick={resetSignature}>
            Clear Signature
          </ClearButton>

          {signError && (
            <Alert variant="outlined" severity="error" sx={{ mt: 1 }}>
              Please add your signature!
            </Alert>
          )}
        </Box>
      </ContentCard>

      <JoinButton
        disabled={disabled}
        onClick={onCreatePlayer}
      >
        {buttonCopy}
      </JoinButton>
    </Root>
  );
};

export default MakePlayer;
